import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import InvestmentProfile from "@/components/capital/InvestmentProfile";

export function TenYearForecast() {
  const { client } = useCapital();
  const [data, setData] = useState([]);
  const [unavail, setUnavail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    api.get("/capital/forecast/10-year", { params: { client } })
      .then((r) => { setData(r.data.items || []); setUnavail(r.data.unavailable ? (r.data.missing_fields || []) : null); })
      .finally(() => setLoading(false));
  }, [client]);

  const total = data.reduce((s, d) => s + (d.budget || 0), 0);
  const avg = data.length ? total / data.length : 0;
  const peak = data.reduce((p, d) => (d.budget || 0) > (p?.budget || 0) ? d : p, data[0] || null);

  return (
    <>
      {unavail && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/60 p-5" data-testid="forecast-unavailable">
          <div className="text-sm font-medium text-amber-800">Forecast unavailable — remaining-life data is currently insufficient for a reliable 10-year projection.</div>
          <div className="text-xs text-slate-600 mt-2">Missing data preventing the forecast:</div>
          <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
            {unavail.map((m) => (
              <li key={m.field}>• <span className="font-medium">{m.field}</span> — missing on {m.missing_count} of {m.total} assets</li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Kpi label="Total Forecast (10y)" value={fmtCurrency(total)} />
        <Kpi label="Annual Avg" value={fmtCurrency(avg)} />
        <Kpi label={peak ? `Peak year ${peak.year}` : "Peak year"} value={peak ? fmtCurrency(peak.budget) : "—"} tone="rose" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="font-display text-lg font-semibold text-slate-900">10-Year Renewal Forecast</h3>
        <p className="text-xs text-slate-500 mb-4">Projected renewal spend by year, derived from remaining life</p>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 30, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="fcst" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#00334C" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#00334C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
              <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Math.round(v/1000)}k`} />
              <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }} />
              <Area type="monotone" dataKey="budget" stroke="#00334C" strokeWidth={2} fill="url(#fcst)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left font-medium px-4 py-2.5">Year</th>
              <th className="text-right font-medium px-4 py-2.5">Assets Renewing</th>
              <th className="text-right font-medium px-4 py-2.5">Forecast Budget</th>
              <th className="text-right font-medium px-4 py-2.5">% of Total</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.year} className="border-b border-slate-100 hover:bg-slate-50/70">
                <td className="px-4 py-2.5 text-slate-800 font-medium">{d.year}</td>
                <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{d.count ?? "—"}</td>
                <td className="px-4 py-2.5 text-right text-slate-900 tabular-nums font-medium">{fmtCurrency(d.budget)}</td>
                <td className="px-4 py-2.5 text-right text-slate-500 tabular-nums">
                  {total > 0 ? `${((d.budget / total) * 100).toFixed(1)}%` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InvestmentProfile client={client} />
    </>
  );
}

function Kpi({ label, value, tone }) {
  const cls = tone === "rose" ? "border-rose-200 bg-gradient-to-br from-rose-50 to-white" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg p-5 border ${cls}`}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-2 font-display text-2xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
